import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  reduce, reject, findIndex, each, cloneDeep,
  find, concat, isArray, uniq, orderBy,
} from 'lodash';
import {
  FormGroup, ControlLabel,
  FormControl, HelpBlock,
} from 'react-bootstrap';
import shortid from 'shortid';
import moment from 'moment';
import { Field } from 'redux-form';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import ValueList from '../custom-meta/value-list';
import {
  addMetaValue, removeMetaValue, updateMetaValue,
  reorderValues,
} from '../custom-meta/actions';
import styles from './styles.css';

const dataTypes = [
  { value: 'string', label: 'Text', showDefaultField: true },
  { value: 'boolean', label: 'Check-box', showDefaultField: false },
  { value: 'date', label: 'Date' },
  { value: 'picklist', label: 'Picklist' },
  // { value: 'multipicklist', label: 'Multi-picklist' },
  { value: 'currency', label: 'Currency', showDefaultField: true },
  { value: 'int', label: 'Whole Number', showDefaultField: true },
  // { value: 'double', label: 'Double', showDefaultField: true },
  // { value: 'percent', label: 'Percent', showDefaultField: true },
  { value: 'number', label: 'Decimal Number', showDefaultField: true },
];

const booleanDefaultValues = [
  { value: true, label: 'True' },
  { value: false, label: 'False' },
  { value: null, label: 'Null' },
];

const RadioGroup = ({
  label, name, options, input, meta: { touched, error }, disabled,
}) => {
  const validationState = touched && error ? 'error' : null;
  return (
    <FormGroup controlId={name} validationState={validationState}>
      <ControlLabel>{label}</ControlLabel>
      <div>
        {options.map((option) => {
          const checked = input.value === option.value || (option.value === null && input.value === '');
          return (
            <div className="radio-inline" key={option.value}>
              <label>
                <input
                  type="radio"
                  name={name}
                  value={option.value}
                  checked={checked}
                  disabled={disabled}
                  onChange={(event) => {
                    let newValue = null;
                    if (event.target.checked) {
                      newValue = option.value;
                    }
                    return input.onChange(newValue);
                  }}
                />
                &nbsp;
                <code>{option.label}</code>
              </label>
            </div>
          );
        })}
      </div>
      {touched && error && <HelpBlock>{error}</HelpBlock>}
    </FormGroup>
  );
};
RadioGroup.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  input: PropTypes.shape({}),
  disabled: PropTypes.bool,
  meta: PropTypes.shape({}),
};
RadioGroup.defaultProps = {
  label: null,
  name: null,
  input: {},
  options: [],
  disabled: false,
  meta: {},
};

class CustomMetaForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      values: [...(props.values || [])],
    };
    this.renderField = this.renderField.bind(this);
    this.renderDatePicker = this.renderDatePicker.bind(this);
    this.handleDateChange = this.handleDateChange.bind(this);
    this.requestNewValue = this.requestNewValue.bind(this);
    this.requestRemoveValue = this.requestRemoveValue.bind(this);
    this.requestUpdateValue = this.requestUpdateValue.bind(this);
    this.requestUpdateDefault = this.requestUpdateDefault.bind(this);
    this.requestUpdatePositions = this.requestUpdatePositions.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.isNew && nextProps.selectedDataType !== this.props.selectedDataType) {
      this.props.onChange('default_value', null);
      this.setState({ values: [] });
    }
  }

  handleDateChange(date) {
    this.props.onChange('default_value', date ? date.format('YYYY-MM-DD') : null);
  }

  requestNewValue(value) {
    const { isNew, metaId } = this.props;
    return new Promise((resolve, rj) => {
      if (isNew) {
        const id = shortid.generate();
        const newValue = { _id: id, value: id, ...value };
        const { values } = this.state;
        const newValues = concat(values, newValue);
        this.setState({ values: newValues });
        this.props.onChange('values', newValues);
        resolve(newValue);
      } else {
        const params = { label: value.label };
        this.props.dispatch(addMetaValue(metaId, params))
          .then(
            (newMeta) => {
              this.setState({ values: newMeta ? newMeta.values || [] : [] });
              resolve(newMeta);
            },
            (errors) => {
              rj(errors);
            },
          );
      }
    });
  }

  requestUpdatePositions(positions) {
    const { isNew, metaId } = this.props;
    const { values } = this.state;
    const oldValues = cloneDeep(values);

    return new Promise((resolve, rj) => {
      each(positions, (val, position) => {
        const value = find(values, { value: val });
        if (value) {
          value.position = position + 1;
        }
      });
      const newValues = orderBy(values, ['position'], ['asc']);
      this.setState({ values: newValues });
      if (!isNew) {
        this.props.dispatch(reorderValues(metaId, positions))
          .then(
            (newMeta) => {
              const rValues = newMeta ? newMeta.values || [] : [];
              this.setState({ values: rValues });
              resolve(rValues);
            },
            (errors) => {
              this.setState({ values: oldValues }); // reverse
              rj(errors);
            },
          );
      } else {
        this.props.onChange('values', newValues);
        resolve(newValues);
      }
    });
  }

  requestRemoveValue(value) {
    const { isNew, metaId } = this.props;
    return new Promise((resolve, rj) => {
      if (isNew) {
        const id = value._id;
        const { values } = this.state;
        const newValues = reject(values, { _id: id });
        this.setState({ values: newValues });
        this.props.onChange('values', newValues);
        resolve(true);
      } else {
        this.props.dispatch(removeMetaValue(metaId, value.value))
          .then(
            (newMeta) => {
              this.setState({ values: newMeta ? newMeta.values || [] : [] });
              resolve(true);
            },
            (errors) => {
              rj(errors);
            },
          );
      }
    });
  }

  requestUpdateValue(value) {
    const { isNew, metaId } = this.props;
    return new Promise((resolve, rj) => {
      if (isNew) {
        const id = value._id;
        const { values } = this.state;
        const idx = findIndex(values, { _id: id });
        if (idx >= 0) {
          const newValues = [...values];
          newValues[idx] = { ...values[idx], ...value };
          this.props.onChange('values', newValues);
          this.setState({ values: newValues });
          resolve(value);
        } else {
          resolve(null);
        }
      } else {
        this.props.dispatch(updateMetaValue(metaId, value.value, { label: value.label }))
          .then(
            (newMeta) => {
              this.setState({ values: newMeta ? newMeta.values || [] : [] });
              resolve(true);
            },
            (errors) => {
              rj(errors);
            },
          );
      }
    });
  }

  requestUpdateDefault(id, isDefault) {
    const { selectedDataType, selectedDefaultValue } = this.props;
    return new Promise((resolve) => {
      const { values } = this.state;
      const idx = findIndex(values, { _id: id });
      if (idx >= 0) {
        const val = values[idx].value;
        let currentDefaults = null;
        let newDefault = null;
        if (selectedDataType === 'picklist') {
          currentDefaults = selectedDefaultValue;
          newDefault = currentDefaults === val ? null : val;
        } else if (selectedDataType === 'multipicklist') {
          currentDefaults = isArray(selectedDefaultValue) ? selectedDefaultValue : [selectedDefaultValue];
          if (isDefault) {
            newDefault = uniq(concat(currentDefaults, val));
          } else {
            // remove the val
            newDefault = reduce(currentDefaults, (items, currentDefault) => {
              if (currentDefault !== val) {
                items.push(currentDefault);
              }
              return items;
            }, []);
          }
        }
        this.props.onChange('default_value', newDefault);
      }
      resolve();
    });
  }

  renderField({
    input, label, componentClass, type, meta: { touched, error }, options, disabled,
  }) {
    const { submitting } = this.props;
    const validationState = touched && error ? 'error' : null;
    return (
      <FormGroup validationState={validationState}>
        <ControlLabel>{label}</ControlLabel>
        {componentClass !== 'select' &&
          <FormControl
            {...input}
            componentClass={componentClass}
            placeholder={label}
            type={type}
            disabled={submitting || disabled}
          />
        }
        {componentClass === 'select' &&
          <FormControl {...input} componentClass={componentClass} placeholder={label} type={type} disabled={submitting || disabled}>
            <option />
            {options.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </FormControl>
        }
        <FormControl.Feedback />
        {touched && error && <HelpBlock>{error}</HelpBlock>}
      </FormGroup>
    );
  }

  renderDatePicker({
    label, date, meta: { touched, error },
  }) {
    const { submitting } = this.props;
    const validationState = touched && error ? 'error' : null;
    return (
      <FormGroup validationState={validationState} className={styles.datePicker}>
        <ControlLabel>{label}</ControlLabel>
        <DatePicker
          dateFormat="DD MMM YYYY"
          className={`form-control clickable ${styles.dateField}`}
          placeholderText="Click to select a date"
          selected={date}
          showYearDropdown
          disabled={submitting}
          scrollableYearDropdown
          isClearable
          popperPlacement="top-end"
          popperModifiers={{
            offset: {
              enabled: true,
              offset: '45px, 0px',
            },
          }}
          onChange={this.handleDateChange}
        />
        {touched && error && <HelpBlock>{error}</HelpBlock>}
      </FormGroup>
    );
  }

  renderDefaultValueField() {
    const {
      selectedDataType, selectedDefaultValue,
      submitting, isNew,
    } = this.props;
    let showDefaultField = false;

    if (selectedDataType) {
      const dataType = find(dataTypes, { value: selectedDataType });
      if (dataType) {
        ({ showDefaultField } = dataType);
      }
    }

    const showBooleanDefaultField = selectedDataType === 'boolean';
    const showDateDefaultField = selectedDataType === 'date';
    if (showDefaultField) {
      return (
        <Field name="default_value" type="text" component={this.renderField} label="Default Value" />
      );
    }
    if (showBooleanDefaultField) {
      return (
        <Field
          name="default_value"
          label="Default Value"
          component={RadioGroup}
          disabled={submitting}
          options={booleanDefaultValues}
        />
      );
    }
    if (showDateDefaultField) {
      const dateMoment = selectedDefaultValue ? moment(selectedDefaultValue, 'YYYY-MM-DD') : null;
      const date = dateMoment && dateMoment.isValid() ? dateMoment : null;
      return (
        <Field
          name="default_value"
          label="Default Date"
          date={date}
          component={this.renderDatePicker}
        />
      );
    }

    if (selectedDataType === 'picklist' || selectedDataType === 'multipicklist') {
      return (
        <ValueList
          isMultiple={selectedDataType === 'multipicklist'}
          values={this.state.values}
          defaultValue={selectedDefaultValue}
          requestNewValue={this.requestNewValue}
          requestRemoveValue={this.requestRemoveValue}
          requestUpdateValue={this.requestUpdateValue}
          requestUpdateDefault={this.requestUpdateDefault}
          requestUpdatePositions={this.requestUpdatePositions}
          isNew={isNew}
          disabled={submitting}
        />
      );
    }

    return null;
  }

  render() {
    const {
      isNew,
    } = this.props;

    return (
      <div>
        <Field
          name="data_type"
          type="text"
          componentClass="select"
          options={dataTypes}
          component={this.renderField}
          label="Data Type"
          disabled={!isNew}
        />
        {this.renderDefaultValueField()}
      </div>
    );
  }
}


function mapStateToProps(state, ownProps) {
  return {
    isNew: ownProps.metaId === undefined || ownProps.metaId === null,
  };
}

CustomMetaForm.propTypes = {
  dispatch: PropTypes.func.isRequired,
  isNew: PropTypes.bool,
  metaId: PropTypes.string,
  submitting: PropTypes.bool,
  selectedDataType: PropTypes.string,
  selectedDefaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
  values: PropTypes.arrayOf(PropTypes.shape({})),
  onChange: PropTypes.func,
};
CustomMetaForm.defaultProps = {
  isNew: false,
  submitting: false,
  selectedDataType: null,
  selectedDefaultValue: null,
  values: [],
  metaId: null,
  onChange: () => {},
};

export default connect(mapStateToProps)(CustomMetaForm);
